import { Box, Paper, Radio } from "@mui/material";
import { useState, useEffect, useMemo, ChangeEvent } from "react";
import { ErrorComponent } from "../../../../components/error/error.component";
import TablePaginationComponent from "../../../../components/table/table-pagination.component";
import { PaginatedResult } from "../../../../models/default/paginated-result";
import { Customer } from "../../../../models/general/customer";
import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";

interface AnalysisCustomerPageComponentProps {
    handleSelectCustomer: (customerId: number, customerName: string) => void;
    selectedCustomer: { id: number | null; name: string | null };
}

const AnalysisCustomerPageComponent = ({ handleSelectCustomer, selectedCustomer  }: AnalysisCustomerPageComponentProps) => {
    const service = new BaseService<Customer>(URLS.CUSTOMER);
    const [dataSource, setDataSource] = useState<Customer[]>([]);
    const [pagination, setPagination] = useState<PaginatedResult<Customer>>();
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [autoSelected, setAutoSelected] = useState(false);

    useEffect(() => {
        setPage(0);
    }, [selectedCustomer]);

    useEffect(() => {
        fetchCustomers();
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (!autoSelected && selectedCustomer.id === null) {
            service.clearParameters();
            service.addParameter("limit", 1);
            service.addParameter("offset", 0);
            service.getAllPaginate()
                .then((response) => {
                    const total = response.data.count;
                    const results = response.data.results || [];
                    if (total === 1 && results.length === 1) {
                        const singleCustomer = results[0];
                        if (singleCustomer?.name?.toUpperCase() === "DELL") {
                            handleSelectCustomer(singleCustomer.id, singleCustomer.name || "");
                            setAutoSelected(true);
                        }
                    }
                })
                .catch(() => {});
        }
    }, [autoSelected, selectedCustomer.id]);

    const fetchCustomers = () => {
        service.clearParameters();
        service.addParameter("limit", rowsPerPage);
        service.addParameter("offset", page * rowsPerPage);

        service
            .getAllPaginate()
            .then((response) => {
                setPagination(response.data);
                setDataSource(response.data.results);
            })
            .catch(ErrorComponent);
    };

    const handleSelectCustomers = (customerId: number, customerName: string) => {
        handleSelectCustomer(customerId, customerName);
    };

    const renderRadioButton = (customer: Customer) => (
        <Box sx={{ display: "flex", alignItems: "center" }} key={customer.id} onClick={() => handleSelectCustomer(customer.id, customer?.name?? "")}>
            <Radio
                color="primary"
                id={`radio-${customer.id}`}
                checked={selectedCustomer.id === customer.id}
                onChange={() => handleSelectCustomers(customer.id, customer?.name?? "")}
            />
            {customer.name}
        </Box>
    );

    const mappedDataSource = useMemo(() =>
        dataSource.map((customer) => ({
            ...customer,
            radioButton: renderRadioButton(customer),
        }))
    , [dataSource, selectedCustomer]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const renderCellHandler = (column: string, index: number, row: any) => {
        return column === "radioButton" ? row.radioButton : row[column];
    };

    const headerCellCustomStyleHandler = (column: any) => {
        const baseStyle = {
            backgroundColor: "#FFFF !important",
            color: "var(--primary-main) !important",
            borderBottom: "1px solid var(--primary-main)",
            borderTop: "1px solid var(--primary-main)",
        };

        return column === "customer"
            ? { ...baseStyle, paddingLeft: "54px" }
            : baseStyle;
    };

    return (
        <section>
            <Paper variant="outlined" sx={{ marginTop: "16px" }}>
                <TablePaginationComponent
                    id="table-analysis-customer"
                    state={mappedDataSource}
                    displayedColumnsShow={["customer"]}
                    displayedColumns={["radioButton"]}
                    renderCellHandler={{ radioButton: renderCellHandler }}
                    headerCellCustomStyleHandler={headerCellCustomStyleHandler}
                    count={pagination?.count}
                    next={pagination?.next}
                    previous={pagination?.previous}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                />
            </Paper>
        </section>
    );
};

export default AnalysisCustomerPageComponent;
